import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Check, X, UserPlus, ArrowRight } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { UserAvatar } from "./UserAvatar";
import { listUsers, adminUpdateUser, formatApiError } from "../lib/api";
import { relativeTime } from "../lib/theme";

export const isPending = (u) => u.approval === "pending";

export function PendingList({ users, onChange }) {
  const act = async (u, approval) => {
    try {
      await adminUpdateUser(u.id, { approval });
      window.dispatchEvent(new Event("pending:changed"));
      toast.success(approval === "approved" ? `${u.name} can now sign in` : `${u.name}'s request declined`);
      onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const pending = users.filter(isPending);
  if (!pending.length) return null;
  return (
    <section className="mt-8 rounded-3xl bg-card p-2 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_-16px_rgba(0,0,0,0.12)]" data-testid="pending-approvals">
      <p className="px-4 pt-3 pb-1 text-[11px] uppercase tracking-[0.16em] text-[#FF9F0A] font-semibold">Pending approval · {pending.length}</p>
      <ul className="divide-y divide-border/60">
        {pending.map((u) => (
          <li key={u.id} className="flex items-center gap-4 px-4 py-3" data-testid={`pending-user-${u.id}`}>
            <UserAvatar user={u} className="h-9 w-9 text-xs" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{u.name}</p>
              <p className="text-xs text-muted-foreground truncate">{u.email} · requested {relativeTime(u.created_at)}</p>
            </div>
            <Button data-testid={`decline-user-${u.id}`} variant="ghost" size="sm" className="rounded-full gap-1 text-muted-foreground" onClick={() => act(u, "declined")}>
              <X className="h-3.5 w-3.5" /> Decline
            </Button>
            <Button data-testid={`approve-user-${u.id}`} size="sm" className="rounded-full gap-1" onClick={() => act(u, "approved")}>
              <Check weight="bold" className="h-3.5 w-3.5" /> Approve
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function usePendingCount(enabled) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    const load = () => listUsers().then((us) => setN(us.filter(isPending).length)).catch(() => {});
    load();
    window.addEventListener("pending:changed", load);
    return () => window.removeEventListener("pending:changed", load);
  }, [enabled]);
  return n;
}

export default function PendingBanner({ count }) {
  if (!count) return null;
  return (
    <Link to="/team" data-testid="pending-approvals-banner"
      className="mb-8 flex items-center gap-4 rounded-3xl bg-card px-5 py-4 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_-16px_rgba(0,0,0,0.12)] hover:-translate-y-0.5 transition-transform">
      <span className="h-10 w-10 rounded-2xl bg-[#FF9F0A]/15 flex items-center justify-center"><UserPlus weight="duotone" className="h-5 w-5 text-[#FF9F0A]" /></span>
      <div className="flex-1">
        <p className="text-sm font-semibold">{count} {count === 1 ? "person is" : "people are"} waiting for approval</p>
        <p className="text-xs text-muted-foreground">New accounts can't see the collection until an admin approves them.</p>
      </div>
      <span className="text-sm text-muted-foreground inline-flex items-center gap-1">Review <ArrowRight className="h-4 w-4" /></span>
    </Link>
  );
}
